import { lazy, Suspense, useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';

import { AnswerViewProvider } from './components/AnswerView/AnswerViewContextProvider';
import { ASK_DATASCOPE_TRIAL_END_DATE } from './components/AskDataScope/trialConstants';
import { AuthLayer } from './components/Auth/AuthLayer';
import { AuthLayout } from './components/Auth/AuthLayout';
import { DynamicOIDCProvider } from './components/Auth/DynamicOIDCProvider';
import { SSOAuthProvider } from './components/Auth/SSOAuthProvider';
import { FormBuilderLayout } from './components/FormBuilderLayout/FormBuilderLayout';
import { NestedLocationsProvider } from './components/Locations/contexts/NestedLocationsContext';
import { MainLayout } from './components/MainLayout/MainLayout';
import { OnboardingTemplatesLayout } from './components/MainLayout/OnboardingTemplatesLayout';
import { VideoLayout } from './components/MainLayout/VideoLayout';
import { NewMobileUserDataContextProvider } from './components/MobileUsers/mobileUsersNew/newMobileUserDataContextProvider';
import { NotFound } from './components/NotFound/NotFound';
import { NavigateWithParams } from './components/Onboarding/NavigateWithParams';
import { OnboardingLayout } from './components/Onboarding/OnboardingLayout';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { UsersProtectedRoute } from './components/ProtectedRoute/UsersProtectedRoute';
import { ScrollToHash } from './components/ScrollToHash';
import { TicketsAccessRouteGuard } from './components/Findings/TicketsAccessRouteGuard';
import { PlanAccessGate } from './components/TaskAssignerNew/PlanAccessGate';
import { TaskAssignerProvider } from './components/TaskAssignerNew/TaskAssignerCreate/TaskAssignerProvider';
import { TaskAssignerProvider as TaskAssignsPricingProvider } from './components/TaskAssignerNew/TaskAssignerContextProvider';
import { Loader } from './components/uiComponents/CommonComponents/Loader';
import { useFeatureFlags } from './hooks';
import { AlertTriangleOutline, CheckmarkCircle2Outline, CloseCircleOutline } from './icons';
import { useFirebaseStore } from './newStore/firebaseStore';
import { useUserDataStore } from './newStore/userDataStore';
import store from './store/store';
import { theme } from './style/theme';
import { initializeBeamer, initializeTrackers } from './utils/eventTracker';
import { initializeIncognitoIntercom, initializeIntercomWithUser } from './utils/intercom';
import { paths } from './utils/navigation';
import { PageViewTracker } from './utils/pageViewTracker';
import { customSnackbar } from './utils/snackbar';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      retry: 0,
    },
  },
});

function DefaultRedirect() {
  const fetchedUserData = useUserDataStore((state) => state.fetchedUserData);
  const { checkFlag } = useFeatureFlags();

  if (!fetchedUserData) return <Loader />;

  const trialExpired = Date.now() > ASK_DATASCOPE_TRIAL_END_DATE.getTime();
  const destination =
    checkFlag('AskDataScope') && !trialExpired ? paths.askDataScope.index : paths.home.home;

  return (
    <NavigateWithParams
      to={destination}
      replace
      preserveAllParams
    />
  );
}

function App() {
  const TaskForms = lazy(() => import('./components/TaskForms/taskForms'));
  const MobileUsers = lazy(() => import('./components/MobileUsers/mobileUsers'));
  const MobileUsersNew = lazy(() => import('./components/MobileUsers/mobileUsersNew'));
  const Templates = lazy(() => import('./components/Templates/templates'));
  const TemplateView = lazy(() => import('./components/Templates/newTemplateView'));
  const TemplateUseLoading = lazy(() => import('./components/NewWebForms/TemplateUseLoading'));
  const OnboardingFormTemplates = lazy(
    () => import('./components/TaskForms/OnboardingFormTemplates'),
  );
  const WebForm = lazy(() => import('./components/NewWebForms/webForm'));
  const WebFormRouter = lazy(() => import('./components/NewWebForms/WebFormRouter'));
  const PublicWebForm = lazy(() => import('./components/NewWebForms/PublicWebForm'));
  const Login = lazy(() => import('./components/Auth/Login'));
  const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
  const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
  const ForceChangePassword = lazy(() => import('./components/Auth/ForceChangePassword'));
  const AccountConfig = lazy(() => import('./components/AccountConfig/AccountConfig'));
  const WhatsappModule = lazy(() => import('./components/WhatsappModule/WhatsappModule'));
  const Notifications = lazy(() => import('./components/Notifications/Notifications'));
  const TaskAssigner = lazy(() => import('./components/TaskAssignerNew/TaskAssigner'));
  const Plannings = lazy(() => import('./components/Plannings/Plannings'));
  const PlanningWizard = lazy(() => import('./components/Plannings/Create/PlanningWizard'));
  const PlanningEditRouter = lazy(
    () => import('./components/Plannings/Edit/PlanningEditRouter'),
  );
  const PlanningDetail = lazy(() => import('./components/Plannings/Detail/PlanningDetail'));
  const TimelinePrintPage = lazy(
    () => import('./components/Plannings/Timeline/TimelinePrintPage').then((m) => ({ default: m.TimelinePrintPage })),
  );
  const PlanningTypesList = lazy(
    () => import('./components/Plannings/PlanningTypes/PlanningTypesList'),
  );
  const PlanningTypeForm = lazy(
    () => import('./components/Plannings/PlanningTypes/PlanningTypeForm'),
  );
  const Findings = lazy(() => import('./components/Findings/Findings'));
  const FindingsSettings = lazy(() => import('./components/Findings/FindingsSettings'));
  const FindingType = lazy(() => import('./components/Findings/FindingTypes/FindingType'));
  const CreateOrEditTaskAssigned = lazy(
    () => import('./components/TaskAssignerNew/TaskAssignerCreate/CreateOrEditTaskAssigned'),
  );
  const WebHooksNew = lazy(() => import('./components/WebHooks/webHooksNew'));
  const AnswerView = lazy(() => import('./components/AnswerView/AnswerView'));
  const VerifySignature = lazy(
    () => import('./components/ElectronicSignature/VerifySignature/VerifySignature'),
  );
  const ElectronicSignature = lazy(
    () => import('./components/ElectronicSignature/ElectronicSignature'),
  );
  const ElectronicSignatureActivationProcess = lazy(
    () => import('./components/ElectronicSignature/ElectronicSignatureActivationProcess'),
  );
  const FindingShared = lazy(() => import('./components/Findings/FindingShared/FindingShared'));
  const Resume = lazy(() => import('./components/Resume/Resume'));
  const ElectronicSignatureDocumentValidation = lazy(
    () => import('./components/ElectronicSignature/DocumentValidation'),
  );
  const Integrations = lazy(() => import('./components/Integrations/Integrations'));
  const Lists = lazy(() => import('./components/Lists/Lists'));
  const NewLocation = lazy(() => import('./components/Lists/NewLocation'));
  const LocationsIndex = lazy(() => import('./components/Locations/LocationsIndex'));
  const LocationMigration = lazy(
    () => import('./components/Locations/LocationMigration/LocationMigration'),
  );
  const ListFormCreate = lazy(() => import('./components/Lists/ListFormCreate'));
  const ListFormEdit = lazy(() => import('./components/Lists/ListFormEdit'));
  const ListDetail = lazy(() => import('./components/Lists/ListDetail/ListDetail'));
  const AuditLogs = lazy(() => import('./components/AuditLogs/AuditLogs'));
  const ImportLogsTab = lazy(() => import('./components/AuditLogs/ImportLogsTab'));
  const LocationsFormCreate = lazy(
    () => import('./components/Locations/LocationView/LocationFormCreate'),
  );
  const LocationsFormEdit = lazy(
    () => import('./components/Locations/LocationView/LocationFormEdit'),
  );
  const LocationsFormDisplay = lazy(
    () => import('./components/Locations/LocationView/LocationFormDisplay'),
  );
  const ImportFolders = lazy(() => import('./components/TaskForms/ImportFolders'));
  const ImportTasks = lazy(() => import('./components/TaskAssignerNew/ImportTasks'));
  const Charts = lazy(() => import('./components/Charts/Charts'));
  const ChartModulesIframe = lazy(() => import('./components/Charts/ChartModulesIframe'));
  const Maps = lazy(() => import('./components/Maps/Maps'));
  // DS-3890: the native module replaces the embedded iframe page - old and
  // new never coexist in the menu/route for the same account (AC-1).
  const SensorsRouterLazy = lazy(() =>
    import('./components/Sensors/SensorsRouter').then((m) => ({ default: m.SensorsRouter })),
  );
  const Training = lazy(() => import('./components/Training/Training'));
  const BillingIndex = lazy(() => import('./components/Billing/BillingIndex'));
  const BillingModifyIframe = lazy(() => import('./components/Billing/BillingModifyIframe'));
  const SecuritySettings = lazy(() => import('./components/Security/SecuritySettings'));
  const ImportGroups = lazy(() => import('./components/MobileUsers/ImportGroups'));
  const AIFormBuilderIframe = lazy(() => import('./components/FormBuilder/AIFormBuilderIframe'));
  const OldAnswerDetail = lazy(() => import('./components/AnswerView/OldAnswerDetail'));
  const PDFEditorIframe = lazy(() => import('./components/FormBuilder/PDFEditorIframe'));
  const Onboarding = lazy(() => import('./components/Onboarding/Onboarding'));
  const Step6Video = lazy(() => import('./components/Onboarding/onboarding/Step6Video'));
  const PublishFormIframe = lazy(() => import('./components/FormBuilder/PublishFormIframe'));
  const EmailAlreadyExists = lazy(() =>
    import('./components/Auth/EmailAlreadyExists').then((m) => ({ default: m.EmailAlreadyExists })),
  );
  const SignUp = lazy(() => import('./components/Auth/SignUp'));
  const SSOCallback = lazy(() => import('./components/Auth/SSOCallback'));
  const OIDCCallback = lazy(() => import('./components/Auth/OIDCCallback'));
  const GroupTaskAssignDetail = lazy(
    () => import('./components/TaskAssignerNew/GroupTaskAssignDetail/GroupTaskAssignDetail'),
  );
  const TaskAssignerHistory = lazy(
    () => import('./components/TaskAssignerNew/TaskAssignerTimeline/TaskAssignerHistoryIframe'),
  );
  const ImportAuditHistory = lazy(
    () => import('./components/ImportAuditHistory/ImportAuditHistory'),
  );
  const ImportAuditDetail = lazy(() => import('./components/ImportAuditHistory/ImportAuditDetail'));
  const SetProfile = lazy(() => import('./components/Auth/SetProfile'));
  const AssetsRouter = lazy(() => import('./components/Assets/AssetsRouter'));
  const Meters = lazy(() => import('./components/Assets/Meters/Meters'));
  const MediaGallery = lazy(() => import('./components/MediaGallery/MediaGallery'));
  const AskDataScope = lazy(() => import('./components/AskDataScope/AskDataScope'));
  const DashboardsPage = lazy(() =>
    import('./components/Dashboards/DashboardsPage').then((m) => ({ default: m.DashboardsPage })),
  );
  const DashboardView = lazy(() =>
    import('./components/Dashboards/DashboardView').then((m) => ({ default: m.DashboardView })),
  );
  const OpsCenter = lazy(() => import('./components/OpsCenter/OpsCenter'));
  const AccountStatus = lazy(() => import('./components/AccountStatus/AccountStatus'));
  const FeatureFlagsTab = lazy(() => import('./components/OpsCenter/FeatureFlagsTab'));
  const SupportCommandsTab = lazy(() => import('./components/OpsCenter/SupportCommandsTab'));
  const AlertsTab = lazy(() => import('./components/OpsCenter/AlertsTab'));
  const CustomerOpsTab = lazy(() => import('./components/OpsCenter/CustomerOpsTab'));
  const SupportAccessTab = lazy(() => import('./components/OpsCenter/SupportAccessTab'));
  const DSAdminTab = lazy(() => import('./components/OpsCenter/DSAdminTab'));
  const currentUser = useUserDataStore((state) => state.currentUser);
  const initializeFirebase = useFirebaseStore((state) => state.initializeFirebase);
  const initializeUserData = useUserDataStore((state) => state.actions.initializeUserData);
  const { checkFlag } = useFeatureFlags();

  useEffect(() => {
    initializeUserData();
    initializeTrackers();
  }, []);

  useEffect(() => {
    if (currentUser?.id || currentUser?.is_support) {
      initializeFirebase();
      initializeBeamer(currentUser?.email, currentUser?.lang);
      // Remove this once migration is complete
      if (!checkFlag('ReactFullMigration'))
        window.location.href = `${process.env.REACT_APP_BACKEND_ENDPOINT}/resume`;
    }
    initializeIncognitoIntercom();
  }, [currentUser?.id, currentUser?.is_support]);

  useEffect(() => {
    if (currentUser?.id || currentUser?.is_support) {
      initializeIntercomWithUser(currentUser);
    }
  }, [currentUser?.id, currentUser?.is_support]);

  useEffect(() => {
    if (currentUser?.id) {
      // @ts-ignore
      if (window._hsq) {
        // @ts-ignore
        window._hsq.push([
          'identify',
          {
            email: currentUser?.email,
            name: currentUser?.username,
            lastname: currentUser?.last_name,
          },
        ]);
      }
    }
  }, [currentUser?.id]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <ReduxProvider store={store}>
          <SSOAuthProvider>
            <DynamicOIDCProvider>
              <SnackbarProvider
                maxSnack={3}
                autoHideDuration={3000}
                anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
                Components={{
                  success: customSnackbar,
                  error: customSnackbar,
                  warning: customSnackbar,
                }}
                iconVariant={{
                  success: (
                    <CheckmarkCircle2Outline
                      fontSize='inherit'
                      color='inherit'
                    />
                  ),
                  error: (
                    <CloseCircleOutline
                      fontSize='inherit'
                      color='inherit'
                    />
                  ),
                  warning: (
                    <AlertTriangleOutline
                      fontSize='inherit'
                      color='inherit'
                    />
                  ),
                }}
              >
                <Router
                  future={{
                    v7_startTransition: false,
                    v7_relativeSplatPath: false,
                  }}
                >
                  <PageViewTracker />
                  <ScrollToHash />
                  <Routes>
                    <Route element={<AuthLayer />}>
                      {/* Onboarding and SignUp routes with shared context */}
                      <Route element={<OnboardingLayout />}>
                        <Route
                          path={paths.onboarding.index}
                          element={
                            <Suspense fallback={<Loader />}>
                              <Onboarding />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.onboarding.welcome}
                          element={
                            <NavigateWithParams
                              to='/onboarding/step1'
                              replace
                            />
                          }
                        />
                        <Route
                          path={paths.onboarding.step}
                          element={
                            <Suspense fallback={<Loader />}>
                              <Onboarding />
                            </Suspense>
                          }
                        />
                      </Route>

                      {/* Other Auth routes without OnboardingProvider */}
                      <Route
                        path={paths.auth.emailExists}
                        element={
                          <Suspense fallback={<Loader />}>
                            <EmailAlreadyExists />
                          </Suspense>
                        }
                      />
                      <Route element={<AuthLayout />}>
                        <Route
                          path={paths.auth.signUp}
                          element={
                            <Suspense fallback={<Loader />}>
                              <SignUp />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.auth.login}
                          element={
                            <Suspense fallback={<Loader />}>
                              <Login />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.auth.forgotPassword}
                          element={
                            <Suspense fallback={<Loader />}>
                              <ForgotPassword />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.auth.resetPassword}
                          element={
                            <Suspense fallback={<Loader />}>
                              <ResetPassword />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.auth.changePassword}
                          element={
                            <Suspense fallback={<Loader />}>
                              <ForceChangePassword />
                            </Suspense>
                          }
                        />
                        <Route
                          path='/auth/cognito/callback'
                          element={
                            <Suspense fallback={<Loader />}>
                              <SSOCallback />
                            </Suspense>
                          }
                        />
                        <Route
                          path='/auth/oidc/callback'
                          element={
                            <Suspense fallback={<Loader />}>
                              <OIDCCallback />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.auth.setProfile}
                          element={
                            <Suspense fallback={<Loader />}>
                              <SetProfile />
                            </Suspense>
                          }
                        />
                      </Route>
                    </Route>
                    <Route
                      path={paths.home.index}
                      element={<MainLayout />}
                    >
                      <Route
                        index
                        element={<DefaultRedirect />}
                      />
                      <Route
                        path={paths.home.home}
                        element={
                          <Suspense fallback={<Loader />}>
                            <Resume />
                          </Suspense>
                        }
                      />
                      <Route path={paths.forms.index}>
                        <Route
                          index
                          element={
                            <Suspense fallback={<Loader />}>
                              <TaskForms />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.forms.builder}
                          element={
                            <Suspense fallback={<Loader />}>
                              <FormBuilderLayout />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.forms.editAnswer}
                          element={
                            <Suspense fallback={<Loader />}>
                              <WebFormRouter />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.forms.newAnswer}
                          element={
                            <Suspense fallback={<Loader />}>
                              <WebFormRouter />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.forms.answer}
                          element={
                            <Suspense fallback={<Loader />}>
                              <AnswerViewProvider>
                                <AnswerView />
                              </AnswerViewProvider>
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.forms.importFolders}
                          element={
                            <Suspense fallback={<Loader />}>
                              <ImportFolders />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.forms.aiFormBuilder}
                          element={
                            <Suspense fallback={<Loader />}>
                              <AIFormBuilderIframe />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.forms.publishForm}
                          element={
                            <Suspense fallback={<Loader />}>
                              <PublishFormIframe />
                            </Suspense>
                          }
                        />
                      </Route>
                      <Route
                        path={paths.forms.oldAnswerDetail}
                        element={
                          <Suspense fallback={<Loader />}>
                            <OldAnswerDetail />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.forms.pdfEditor}
                        element={
                          <Suspense fallback={<Loader />}>
                            <PDFEditorIframe />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.integrations.webhookNew}
                        element={
                          <Suspense fallback={<Loader />}>
                            <WebHooksNew />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.integrations.webhookEdit}
                        element={
                          <Suspense fallback={<Loader />}>
                            <WebHooksNew />
                          </Suspense>
                        }
                      />
                      <Route
                        path={`${paths.integrations.index}/*`}
                        element={
                          <Suspense fallback={<Loader />}>
                            <Integrations />
                          </Suspense>
                        }
                      />
                      {checkFlag('Plannings') && (
                        <Route path={paths.plannings.index}>
                          <Route
                            index
                            element={
                              <Suspense fallback={<Loader />}>
                                <Plannings />
                              </Suspense>
                            }
                          />
                          <Route
                            path={paths.plannings.new}
                            element={
                              <Suspense fallback={<Loader />}>
                                <PlanningWizard />
                              </Suspense>
                            }
                          />
                          <Route
                            path={paths.plannings.edit}
                            element={
                              <Suspense fallback={<Loader />}>
                                <PlanningEditRouter />
                              </Suspense>
                            }
                          />
                          <Route
                            path={paths.plannings.detail}
                            element={
                              <Suspense fallback={<Loader />}>
                                <PlanningDetail />
                              </Suspense>
                            }
                          />
                          <Route
                            path={paths.plannings.types}
                            element={
                              <Suspense fallback={<Loader />}>
                                <PlanningTypesList />
                              </Suspense>
                            }
                          />
                          <Route
                            path={paths.plannings.typeNew}
                            element={
                              <Suspense fallback={<Loader />}>
                                <PlanningTypeForm />
                              </Suspense>
                            }
                          />
                          <Route
                            path={paths.plannings.typeEdit}
                            element={
                              <Suspense fallback={<Loader />}>
                                <PlanningTypeForm />
                              </Suspense>
                            }
                          />
                        </Route>
                      )}
                      <Route path={paths.taskAssigner.index}>
                        <Route
                          index
                          element={
                            <Suspense fallback={<Loader />}>
                              <TaskAssigner />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.taskAssigner.new}
                          element={
                            <Suspense fallback={<Loader />}>
                              {/* TaskAssignsPricingProvider/PlanAccessGate gate this
                                  direct URL on plan access -- without it, a
                                  plan-blocked account reached the create form
                                  regardless, since TaskAssignerProvider here is a
                                  same-named but unrelated local provider (task
                                  form state, not plan access). */}
                              <TaskAssignsPricingProvider gateOnly>
                                <PlanAccessGate>
                                  <TaskAssignerProvider>
                                    <CreateOrEditTaskAssigned />
                                  </TaskAssignerProvider>
                                </PlanAccessGate>
                              </TaskAssignsPricingProvider>
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.taskAssigner.edit}
                          element={
                            <Suspense fallback={<Loader />}>
                              <TaskAssignsPricingProvider gateOnly>
                                <PlanAccessGate>
                                  <TaskAssignerProvider>
                                    <CreateOrEditTaskAssigned />
                                  </TaskAssignerProvider>
                                </PlanAccessGate>
                              </TaskAssignsPricingProvider>
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.taskAssigner.import}
                          element={
                            <Suspense fallback={<Loader />}>
                              <ImportTasks />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.taskAssigner.history}
                          element={
                            <Suspense fallback={<Loader />}>
                              <TaskAssignerHistory />
                            </Suspense>
                          }
                        />
                        {checkFlag('GroupTaskAssign') && (
                          <>
                            {/* Group routes are gated by the same FF the
                                backend's require_group_task_feature enforces.
                                Without this, a direct URL on an account
                                without the flag would mount the detail/edit
                                page just to receive a 403 from the API. */}
                            <Route
                              path={paths.taskAssigner.groupDetail}
                              element={
                                <Suspense fallback={<Loader />}>
                                  <GroupTaskAssignDetail />
                                </Suspense>
                              }
                            />
                            <Route
                              path={paths.taskAssigner.groupEdit}
                              element={
                                <Suspense fallback={<Loader />}>
                                  <TaskAssignsPricingProvider gateOnly>
                                    <PlanAccessGate>
                                      <TaskAssignerProvider>
                                        <CreateOrEditTaskAssigned />
                                      </TaskAssignerProvider>
                                    </PlanAccessGate>
                                  </TaskAssignsPricingProvider>
                                </Suspense>
                              }
                            />
                          </>
                        )}
                      </Route>
                      <Route path={paths.findings.index}>
                        <Route
                          index
                          element={
                            <Suspense fallback={<Loader />}>
                              <Findings />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.findings.settings}
                          element={
                            <Suspense fallback={<Loader />}>
                              <TicketsAccessRouteGuard>
                                <FindingsSettings />
                              </TicketsAccessRouteGuard>
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.findings.type}
                          element={
                            <Suspense fallback={<Loader />}>
                              <TicketsAccessRouteGuard>
                                <FindingType />
                              </TicketsAccessRouteGuard>
                            </Suspense>
                          }
                        />
                      </Route>
                      <Route path={paths.signature.index}>
                        <Route
                          index
                          element={
                            <Suspense fallback={<Loader />}>
                              <ElectronicSignature />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.signature.verifyUser}
                          element={
                            <Suspense fallback={<Loader />}>
                              <VerifySignature />
                            </Suspense>
                          }
                        />
                        <Route
                          path={paths.signature.activationRequest}
                          element={
                            <Suspense fallback={<Loader />}>
                              <ElectronicSignatureActivationProcess />
                            </Suspense>
                          }
                        />
                      </Route>
                      <Route
                        path={paths.lists.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <Lists />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.lists.create}
                        element={
                          <Suspense fallback={<Loader />}>
                            <ListFormCreate />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.lists.edit}
                        element={
                          <Suspense fallback={<Loader />}>
                            <ListFormEdit />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.auditLogs.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <AuditLogs />
                          </Suspense>
                        }
                      >
                        <Route
                          path='imports'
                          element={
                            <Suspense fallback={<Loader />}>
                              <ImportLogsTab />
                            </Suspense>
                          }
                        />
                        <Route
                          path='task_imports'
                          element={
                            <Suspense fallback={<Loader />}>
                              <ImportAuditHistory />
                            </Suspense>
                          }
                        />
                        <Route
                          path='task_imports/:auditId'
                          element={
                            <Suspense fallback={<Loader />}>
                              <ImportAuditDetail />
                            </Suspense>
                          }
                        />
                      </Route>
                      <Route
                        path={paths.lists.detail}
                        element={
                          <Suspense fallback={<Loader />}>
                            <ListDetail />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.locations.index}
                        element={
                          <NestedLocationsProvider>
                            <Suspense fallback={<Loader />}>
                              <LocationsIndex />
                            </Suspense>
                          </NestedLocationsProvider>
                        }
                      />
                      <Route
                        path={paths.locations.migration}
                        element={
                          <Suspense fallback={<Loader />}>
                            <LocationMigration />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.locations.createRoot}
                        element={
                          <NestedLocationsProvider>
                            <Suspense fallback={<Loader />}>
                              <LocationsFormCreate />
                            </Suspense>
                          </NestedLocationsProvider>
                        }
                      />
                      <Route
                        path={paths.locations.create}
                        element={
                          <NestedLocationsProvider>
                            <Suspense fallback={<Loader />}>
                              <LocationsFormCreate />
                            </Suspense>
                          </NestedLocationsProvider>
                        }
                      />
                      <Route
                        path={paths.locations.edit}
                        element={
                          <NestedLocationsProvider>
                            <Suspense fallback={<Loader />}>
                              <LocationsFormEdit />
                            </Suspense>
                          </NestedLocationsProvider>
                        }
                      />
                      <Route
                        path={paths.locations.view}
                        element={
                          <NestedLocationsProvider>
                            <Suspense fallback={<Loader />}>
                              <LocationsFormDisplay />
                            </Suspense>
                          </NestedLocationsProvider>
                        }
                      />
                      <Route
                        path={paths.lists.newLocation}
                        element={
                          <Suspense fallback={<Loader />}>
                            <NewLocation />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.askDataScope.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <AskDataScope />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.dashboards.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <DashboardsPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path={`${paths.dashboards.index}/:id`}
                        element={
                          <Suspense fallback={<Loader />}>
                            <DashboardView />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.opsCenter.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <OpsCenter />
                          </Suspense>
                        }
                      >
                        <Route
                          path='feature_flags'
                          element={
                            <Suspense fallback={<Loader />}>
                              <FeatureFlagsTab />
                            </Suspense>
                          }
                        />
                        <Route
                          path='support_commands'
                          element={
                            <Suspense fallback={<Loader />}>
                              <SupportCommandsTab />
                            </Suspense>
                          }
                        />
                        <Route
                          path='alerts'
                          element={
                            <Suspense fallback={<Loader />}>
                              <AlertsTab />
                            </Suspense>
                          }
                        />
                        <Route
                          path='customer_ops'
                          element={
                            <Suspense fallback={<Loader />}>
                              <CustomerOpsTab />
                            </Suspense>
                          }
                        />
                        <Route
                          path='support_access'
                          element={
                            <Suspense fallback={<Loader />}>
                              <SupportAccessTab />
                            </Suspense>
                          }
                        />
                        <Route
                          path='ds_admin'
                          element={
                            <Suspense fallback={<Loader />}>
                              <DSAdminTab />
                            </Suspense>
                          }
                        />
                      </Route>
                      <Route
                        path={paths.accountStatus.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <AccountStatus />
                          </Suspense>
                        }
                      />
                      <Route
                        path='/*'
                        element={
                          <Suspense fallback={<Loader />}>
                            <AssetsRouter />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.meters.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <Meters />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.charts.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <Charts />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.charts.chartModules}
                        element={
                          <Suspense fallback={<Loader />}>
                            <ChartModulesIframe />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.map.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <Maps />
                          </Suspense>
                        }
                      />
                      <Route
                        path='/sensors/*'
                        element={
                          <Suspense fallback={<Loader />}>
                            <SensorsRouterLazy />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.training.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <Training />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.changeLog.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <Notifications />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.account.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <AccountConfig />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.whatsapp.index}
                        element={
                          <ProtectedRoute allowAdmin allowSuperAdmin allowSupport>
                            <Suspense fallback={<Loader />}>
                              <WhatsappModule />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route path={paths.users.index}>
                        <Route
                          index
                          element={
                            <UsersProtectedRoute>
                              <Suspense fallback={<Loader />}>
                                <MobileUsers />
                              </Suspense>
                            </UsersProtectedRoute>
                          }
                        />
                        <Route
                          path={paths.users.user}
                          element={
                            <UsersProtectedRoute userId={currentUser?.id}>
                              <Suspense fallback={<Loader />}>
                                <NewMobileUserDataContextProvider>
                                  <MobileUsersNew />
                                </NewMobileUserDataContextProvider>
                              </Suspense>
                            </UsersProtectedRoute>
                          }
                        />
                        <Route
                          path={paths.users.groupsImport}
                          element={
                            <Suspense fallback={<Loader />}>
                              <ImportGroups />
                            </Suspense>
                          }
                        />
                      </Route>
                      <Route
                        path={paths.billing.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <BillingIndex />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.billing.modifySubscription}
                        element={
                          <Suspense fallback={<Loader />}>
                            <BillingModifyIframe />
                          </Suspense>
                        }
                      />
                      <Route
                        path={paths.account.accountSecurity}
                        element={
                          <Suspense fallback={<Loader />}>
                            <SecuritySettings />
                          </Suspense>
                        }
                      />
                    </Route>
                    <Route
                      path={paths.onboarding.welcome}
                      element={<VideoLayout />}
                    >
                      <Route
                        index
                        element={
                          <Suspense fallback={<Loader />}>
                            <Step6Video />
                          </Suspense>
                        }
                      />
                    </Route>
                    <Route element={<OnboardingTemplatesLayout />}>
                      <Route
                        path={paths.onboarding.templates}
                        element={
                          <Suspense fallback={<Loader />}>
                            <OnboardingFormTemplates />
                          </Suspense>
                        }
                      />
                    </Route>
                    <Route
                      path={paths.public.webforms}
                      element={
                        <Suspense fallback={<Loader />}>
                          <WebForm />
                        </Suspense>
                      }
                    />
                    <Route
                      path={paths.public.webformsPublic}
                      element={
                        <Suspense fallback={<Loader />}>
                          <PublicWebForm />
                        </Suspense>
                      }
                    />
                    <Route
                      path={paths.public.answer}
                      element={
                        <Suspense fallback={<Loader />}>
                          <AnswerViewProvider>
                            <AnswerView />
                          </AnswerViewProvider>
                        </Suspense>
                      }
                    />
                    <Route
                      path={`${paths.plannings.index}/${paths.plannings.timelinePrint}`}
                      element={
                        <Suspense fallback={<Loader />}>
                          <TimelinePrintPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path={paths.public.documentValidation}
                      element={
                        <Suspense fallback={<Loader />}>
                          <ElectronicSignatureDocumentValidation />
                        </Suspense>
                      }
                    />
                    <Route
                      path={paths.public.finding}
                      element={
                        <Suspense fallback={<Loader />}>
                          <FindingShared />
                        </Suspense>
                      }
                    />
                    <Route
                      path='/templates'
                      element={
                        <Suspense fallback={<Loader />}>
                          <Templates />
                        </Suspense>
                      }
                    />
                    <Route
                      path='/template_view'
                      element={
                        <Suspense fallback={<Loader />}>
                          <TemplateView />
                        </Suspense>
                      }
                    />
                    <Route
                      path={paths.templates.use}
                      element={
                        <Suspense fallback={<Loader />}>
                          <TemplateUseLoading />
                        </Suspense>
                      }
                    />
                    {process.env.NODE_ENV === 'development' && (
                      <Route
                        path={paths.mediaGallery.index}
                        element={
                          <Suspense fallback={<Loader />}>
                            <MediaGallery />
                          </Suspense>
                        }
                      />
                    )}
                    <Route
                      path='*'
                      element={<NotFound />}
                    />
                  </Routes>
                </Router>
              </SnackbarProvider>
            </DynamicOIDCProvider>
          </SSOAuthProvider>
        </ReduxProvider>
      </ThemeProvider>
      <ReactQueryDevtools
        initialIsOpen={false}
        buttonPosition='bottom-left'
      />
    </QueryClientProvider>
  );
}

export default App;
